import { useEffect, useState } from "react";
import styled from "styled-components";
import { isValidInteger } from "../../../utils/tools";
import { BarcodeEngine } from "./barcode-engine";

// Modal Styles
const BarcodeModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BarcodeInputContainer = styled.div`
  position: relative;
  height: 80%;
  background-color: black;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;

  /* Tailwind responsive for mobile */
  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    max-width: none; /* Full width on mobile */
    border-radius: 0; /* Remove border-radius on mobile for fullscreen effect */
  }
`;

const CameraPreview = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9998;
  border-radius: 0;
  @media (min-width: 769px) {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 12px;
    z-index: 1;
  }
`;

const ScanZoneWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 9999;
`;

const ScanZoneBox = styled.div`
  position: relative;
  box-sizing: border-box;
`;

const ScanZoneCorners = styled.div`
  position: absolute;
  inset: 0;
  &::before, &::after,
  & > span::before, & > span::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-color: #ffffff;
    border-style: solid;
  }
  &::before { top: 0; left: 0; border-width: 3px 0 0 3px; }
  &::after  { top: 0; right: 0; border-width: 3px 3px 0 0; }
  & > span::before { bottom: 0; left: 0; border-width: 0 0 3px 3px; }
  & > span::after  { bottom: 0; right: 0; border-width: 0 3px 3px 0; }
`;

const ScanLine = styled.div`
  position: absolute;
  left: 4px;
  right: 4px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #ff4444, transparent);
  animation: scanMove 1.8s linear infinite;
  @keyframes scanMove {
    0%   { top: 8px; opacity: 1; }
    90%  { opacity: 1; }
    100% { top: calc(100% - 8px); opacity: 0; }
  }
`;

const ZoneLabel = styled.span`
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255,255,255,0.8);
  font-size: 11px;
  white-space: nowrap;
  letter-spacing: 0.5px;
`;


const ButtonContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 10px;
  z-index: 9999;
`;

const Button = styled.button`
  background-color: transparent;
  color: white;
  font-size: 20px;
  border: none;
  cursor: pointer;
`;

const FlipButton = styled(Button)`
  font-size: 25px;
`;

const CloseButton = styled(Button)`
  font-size: 30px;
`;

const getSavedCameraIndex = () => {
  const str = localStorage.getItem("CAMERA_INDEX");
  if (!str || !isValidInteger(str)) return -1;
  return parseInt(str);
};

const saveCameraIndex = (index) => {
  localStorage.setItem("CAMERA_INDEX", `${index}`);
};

export const BarcodeInput = ({
  show,
  onDismiss,
  cameraOptions,
  onScanComplete,
  onTimeOut,
}) => {
  const [cameraId, setCameraId] = useState(null);

  const onCloseHandler = () => {
    onDismiss && onDismiss();
  };

  const selectCameraByIndex = (index) => {
    if (cameraOptions.length === 0) return;
    if (index < 0 || index >= cameraOptions.length) index = 0;
    saveCameraIndex(index);
    setCameraId(cameraOptions[index].deviceId);
  };

  const selectNextCamera = () => {
    if (cameraOptions.length === 0) return;
    let index = getSavedCameraIndex();
    if (cameraOptions.length === 1) {
      index = 0;
    } else {
      index = (index + 1) % cameraOptions.length;
    }
    selectCameraByIndex(index);
  };

  useEffect(() => {
    let index = getSavedCameraIndex();
    if (index < 0 || index >= cameraOptions.length) index = 0;
    selectCameraByIndex(index);
  }, [cameraOptions]);

  const onFlipCameraHandler = () => {
    selectNextCamera();
  };

  if (!cameraOptions || cameraOptions.length === 0 || !show) return null;

  return (
    <BarcodeModal>
      <BarcodeInputContainer>
        <CameraPreview>
          <ScanZoneWrapper>
            <ScanZoneBox style={{ width: 290, height: 290, marginTop: 28 }}>
              <ScanZoneCorners><span /></ScanZoneCorners>
              <ScanLine />
            </ScanZoneBox>
          </ScanZoneWrapper>
          <BarcodeEngine
            timeout={15000}
            deviceId={cameraId}
            playSound={true}
            onScanComplete={onScanComplete}
            onTimeOut={onTimeOut}
          />
        </CameraPreview>

        <ButtonContainer>
          {cameraOptions && cameraOptions.length > 1 && (
            <FlipButton onClick={onFlipCameraHandler}>
              <i className="bi bi-phone-flip"></i>
            </FlipButton>
          )}
          <CloseButton onClick={onCloseHandler}>
            <i className="bi bi-x-lg"></i>
          </CloseButton>
        </ButtonContainer>
      </BarcodeInputContainer>
    </BarcodeModal>
  );
};
